// STUDIO-BLUETOOTH-REMOTE-INPUT-DIAG-1/-POINTER-DIAG-2 · 진단 ON 동안 window capture 단계에서 입력을 가로채 기록.
// 기존 Studio 핸들러로 전달하지 않음(stopImmediatePropagation) → 상품·캡처·LIVE 동작 0.
import { useCallback, useEffect, useRef, useState } from 'react';
import { fromKeyEvent, fromOtherEvent, gestureRecord, pushRecord, withTiming } from '@/lib/remoteInputDiag';

const OTHER = ['pointerdown', 'pointerup', 'pointercancel', 'click', 'wheel', 'contextmenu'];

export default function useRemoteInputLog(active, { blockDefault, panelRef, segment }) {
  const [records, setRecords] = useState([]);
  const [pageState, setPageState] = useState({ focus: true, visible: true });
  const [hoverMoves, setHoverMoves] = useState(0);
  // 셔터 후 손가락 터치 전달 확인: 눌린 채 남은 펜 · 마지막 펜 이후 손가락 수신 · touchstart 수.
  const [pen, setPen] = useState({ active: [], lastPenAt: null, fingerAfterPen: 0, touchStarts: 0 });
  const seq = useRef(0);
  const downAt = useRef({});
  const gestures = useRef({});
  const prevT = useRef(null);
  const startT = useRef(performance.now());
  const segRef = useRef(segment);
  segRef.current = segment;

  const add = useCallback((rec) => {
    const r = withTiming({ ...rec, seg: segRef.current }, prevT.current, startT.current);
    prevT.current = rec.t;
    setRecords((l) => pushRecord(l, r));
  }, []);

  useEffect(() => {
    if (!active) { return undefined; }
    if (document.activeElement && document.activeElement !== document.body) document.activeElement.blur();
    const isCtl = (e) => e.target && e.target.closest && e.target.closest('[data-diag-control]');
    const onKey = (e) => {
      const now = performance.now();
      const id = `${e.key}|${e.code}`;
      if (e.type === 'keydown' && !e.repeat) downAt.current[id] = now;
      seq.current += 1;
      add(fromKeyEvent(e, seq.current, now, downAt.current[id]));
      if (e.type === 'keyup') delete downAt.current[id];
      e.stopImmediatePropagation();
      if (blockDefault) e.preventDefault();
    };
    const onOther = (e) => {
      if (isCtl(e)) return;
      const now = performance.now();
      seq.current += 1;
      add(fromOtherEvent(e, seq.current, now));
      if (e.pointerType === 'pen' && e.type === 'pointerdown') {
        setPen((p) => ({ ...p, active: [...new Set([...p.active, e.pointerId])], lastPenAt: now, fingerAfterPen: 0 }));
      } else if (e.pointerType === 'pen' && (e.type === 'pointerup' || e.type === 'pointercancel')) {
        setPen((p) => ({ ...p, active: p.active.filter((id) => id !== e.pointerId), lastPenAt: now }));
      } else if (e.pointerType === 'touch' && e.type === 'pointerdown') {
        setPen((p) => ({ ...p, fingerAfterPen: p.fingerAfterPen + 1 }));
      }
      if (e.type === 'pointerdown') {
        gestures.current[e.pointerId] = { x: Math.round(e.clientX * 10) / 10, y: Math.round(e.clientY * 10) / 10,
          sx: Math.round(e.screenX * 10) / 10, sy: Math.round(e.screenY * 10) / 10,
          lastX: e.clientX, lastY: e.clientY, t: now, moves: 0, pointerType: e.pointerType, pointerId: e.pointerId,
          target: fromOtherEvent(e, 0, now).target };
      }
      if ((e.type === 'pointerup' || e.type === 'pointercancel') && gestures.current[e.pointerId]) {
        seq.current += 1;
        add(gestureRecord(gestures.current[e.pointerId], e, seq.current, now));
        delete gestures.current[e.pointerId];
      }
      e.stopImmediatePropagation();
      if (blockDefault && e.cancelable && e.type !== 'wheel') e.preventDefault();
    };
    const onMove = (e) => {
      const g = gestures.current[e.pointerId];
      if (g) { g.moves += 1; g.lastX = e.clientX; g.lastY = e.clientY; } else setHoverMoves((n) => n + 1);
      if (!isCtl(e)) e.stopImmediatePropagation();
    };
    const onState = (e) => {
      setPageState({ focus: document.hasFocus(), visible: document.visibilityState === 'visible' });
      if (e && e.type === 'popstate') { seq.current += 1; add(fromOtherEvent(e, seq.current, performance.now())); }
    };
    window.addEventListener('keydown', onKey, true);
    window.addEventListener('keyup', onKey, true);
    OTHER.forEach((t) => window.addEventListener(t, onOther, { capture: true, passive: t === 'wheel' }));
    window.addEventListener('pointermove', onMove, { capture: true, passive: true });
    const onTouch = () => setPen((p) => ({ ...p, touchStarts: p.touchStarts + 1 }));
    window.addEventListener('touchstart', onTouch, { capture: true, passive: true });
    ['focus', 'blur', 'popstate'].forEach((t) => window.addEventListener(t, onState));
    document.addEventListener('visibilitychange', onState);
    onState();
    return () => {
      window.removeEventListener('keydown', onKey, true);
      window.removeEventListener('keyup', onKey, true);
      OTHER.forEach((t) => window.removeEventListener(t, onOther, { capture: true }));
      window.removeEventListener('pointermove', onMove, { capture: true });
      window.removeEventListener('touchstart', onTouch, { capture: true });
      ['focus', 'blur', 'popstate'].forEach((t) => window.removeEventListener(t, onState));
      document.removeEventListener('visibilitychange', onState);
    };
  }, [active, blockDefault, add]);

  const clear = useCallback(() => {
    setRecords([]); setHoverMoves(0); seq.current = 0; downAt.current = {}; gestures.current = {};
    setPen({ active: [], lastPenAt: null, fingerAfterPen: 0, touchStarts: 0 });
    prevT.current = null; startT.current = performance.now();
  }, []);
  return { records, pageState, hoverMoves, pen, clear };
}
